import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import useScrollReveal from "../../hooks/useScrollReveal";
import {
  ARTICLES,
  CATEGORIES,
  getCategory,
  getFeaturedArticle,
} from "../../data/insights";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

function CategoryChip({ slug, active, onClick }) {
  const cat = getCategory(slug);
  const label = slug === "all" ? "All Insights" : cat?.label || slug;
  return (
    <button
      onClick={onClick}
      data-testid={`insights-category-${slug}`}
      className={`rounded-full border px-4 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] transition-colors min-h-[34px] ${
        active
          ? "border-clay bg-clay/10 text-clay"
          : "border-sand-300 bg-sand-50 text-ink-muted hover:border-clay/40 hover:text-ink"
      }`}
    >
      {label}
    </button>
  );
}

function FeaturedCard({ article }) {
  const cat = getCategory(article.category);
  return (
    <Link
      to={`/insights/${article.slug}`}
      data-testid="insights-featured-card"
      className="reveal group block rounded-3xl border border-sand-300 bg-sand-50 p-8 sm:p-10 md:p-12 hover:border-clay/40 transition-colors"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-clay">
          Featured Insight
        </span>
        {cat && (
          <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
            · {cat.label}
          </span>
        )}
      </div>
      <h2 className="font-display mt-6 text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-semibold text-ink group-hover:text-rust transition-colors">
        {article.title}
      </h2>
      <p className="mt-5 text-base sm:text-lg text-ink-muted leading-relaxed max-w-3xl">
        {article.description}
      </p>
      <div className="mt-7 flex items-center gap-4 text-[12.5px] text-ink-muted font-mono uppercase tracking-[0.14em]">
        <span className="inline-flex items-center gap-1.5">
          <Clock size={12} /> {article.readingTime} min read
        </span>
        <span>·</span>
        <span>{fmtDate(article.publishedAt)}</span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-clay group-hover:translate-x-0.5 transition-transform">
          Read insight <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

function ArticleCard({ article }) {
  const cat = getCategory(article.category);
  return (
    <Link
      to={`/insights/${article.slug}`}
      data-testid={`insights-article-${article.slug}`}
      className="group reveal flex flex-col rounded-3xl border border-sand-300 bg-sand-50 p-7 sm:p-8 hover:border-clay/40 transition-colors h-full"
    >
      {cat && (
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
          {cat.label}
        </span>
      )}
      <h3 className="font-display mt-4 text-xl sm:text-2xl leading-[1.15] tracking-tight font-semibold text-ink group-hover:text-rust transition-colors">
        {article.title}
      </h3>
      <p className="mt-3 text-[14px] text-ink-muted leading-relaxed flex-1">
        {article.description}
      </p>
      <div className="mt-6 flex items-center justify-between font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink-muted">
        <span className="inline-flex items-center gap-1.5">
          <Clock size={11} /> {article.readingTime} min
        </span>
        <span className="inline-flex items-center gap-1.5 text-clay">
          Read <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function FinalCtaBand({ onAudit }) {
  return (
    <section
      data-testid="insights-final-cta"
      className="px-5 sm:px-6 md:px-10 pb-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="reveal rounded-3xl bg-ink-deep text-sand-100 p-8 sm:p-12 md:p-14">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <p className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-clay">
                Ready to close the gap
              </p>
              <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-semibold">
                Reading insights is step one. Step two is picking the build that matches the leak.
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
              <Link
                to="/websites"
                data-testid="insights-cta-websites"
                className="inline-flex items-center justify-between gap-2 rounded-full bg-sand-100 text-ink px-6 py-3.5 text-[14px] font-medium hover:bg-clay hover:text-sand-100 transition-colors min-h-[48px]"
              >
                Website Packages
                <ArrowRight size={15} />
              </Link>
              <Link
                to="/revenue-systems"
                data-testid="insights-cta-revenue"
                className="inline-flex items-center justify-between gap-2 rounded-full border border-sand-300/30 bg-transparent text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-sand-100/10 transition-colors min-h-[48px]"
              >
                Revenue Systems
                <ArrowRight size={15} />
              </Link>
              <button
                onClick={onAudit}
                data-testid="insights-cta-audit"
                data-magnetic
                className="inline-flex items-center justify-between gap-2 rounded-full bg-clay text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-rust transition-colors min-h-[48px]"
              >
                Apply for a Revenue Audit
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function InsightsPage() {
  useScrollReveal();
  const navigate = useNavigate();
  const [active, setActive] = useState("all");

  const featured = getFeaturedArticle();

  const visible = useMemo(() => {
    const rest = ARTICLES.filter((a) => a.slug !== featured?.slug);
    if (active === "all") return rest;
    return rest.filter(
      (a) => a.category === active || (a.tags || []).includes(active),
    );
  }, [active, featured]);

  const openAudit = () => {
    window.dispatchEvent(new CustomEvent("open-audit", { detail: {} }));
  };

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="insights-page">
      <SEO page="/insights" />
      <Nav />
      <main>
        {/* Hero */}
        <section className="pt-28 sm:pt-32 md:pt-36 pb-14 sm:pb-16 px-5 sm:px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="reveal max-w-4xl">
              <p className="eyebrow !text-clay inline-flex items-center gap-2">
                <BookOpen size={13} /> Modern Exit · Insights
              </p>
              <h1
                data-kinetic
                className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl lg:text-[4rem] leading-[1.02] tracking-[-0.02em] font-semibold text-ink"
              >
                The premium insights library for operators who want{" "}
                <span className="italic text-ink-muted font-normal">
                  more booked revenue, not more traffic.
                </span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-ink-muted leading-relaxed max-w-3xl">
                Insights for service businesses turning websites, leads, and follow-up into booked revenue.
                Written by the studio that builds the systems — not the one that writes the headlines.
              </p>
            </div>
          </div>
        </section>

        {/* Featured */}
        <section className="px-5 sm:px-6 md:px-10 pb-14 sm:pb-16" data-testid="insights-featured">
          <div className="max-w-7xl mx-auto">
            {featured && <FeaturedCard article={featured} />}
          </div>
        </section>

        {/* Category filter + grid */}
        <section className="px-5 sm:px-6 md:px-10 pb-20" data-testid="insights-library">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-8 reveal">
              <div>
                <p className="eyebrow !text-clay">Library</p>
                <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl leading-[1.1] tracking-tight font-semibold text-ink">
                  Browse by topic
                </h2>
              </div>
              <p className="text-[14px] text-ink-muted max-w-sm">
                <span
                  data-count={ARTICLES.length}
                  data-count-suffix={` ${ARTICLES.length === 1 ? "insight" : "insights"}`}
                  className="tabular-nums font-semibold text-ink"
                />
                {" · updated monthly"}
              </p>
            </div>

            <div className="reveal flex items-center gap-2 flex-wrap mb-10" data-testid="insights-category-bar">
              <CategoryChip slug="all" active={active === "all"} onClick={() => setActive("all")} />
              {CATEGORIES.map((c) => (
                <CategoryChip
                  key={c.slug}
                  slug={c.slug}
                  active={active === c.slug}
                  onClick={() => setActive(c.slug)}
                />
              ))}
            </div>

            {visible.length === 0 ? (
              <div
                data-testid="insights-empty-state"
                className="reveal rounded-3xl border border-dashed border-sand-300 bg-sand-50/60 p-12 text-center"
              >
                <p className="font-display text-xl font-semibold text-ink">
                  More insights coming soon.
                </p>
                <p className="mt-3 text-[14px] text-ink-muted max-w-md mx-auto leading-relaxed">
                  We publish every few weeks. While you wait, read the featured piece above — or
                  skip straight to picking the build that fits your business.
                </p>
                <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                  <Link
                    to="/packages"
                    className="inline-flex items-center gap-2 rounded-full bg-ink-deep text-sand-100 px-5 py-2.5 text-[13px] font-medium hover:bg-rust transition-colors min-h-[40px]"
                  >
                    View packages <ArrowRight size={13} />
                  </Link>
                  <button
                    onClick={() => setActive("all")}
                    className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-5 py-2.5 text-[13px] text-ink hover:border-clay/40 transition-colors min-h-[40px]"
                  >
                    Show all insights
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {visible.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            )}
          </div>
        </section>

        <FinalCtaBand onAudit={openAudit} />
      </main>
      <Footer />
    </div>
  );
}
